import {
  Group,
  Button,
  Text,
  Stack,
  Paper,
  Box,
  ActionIcon,
} from '@mantine/core';
import { useState, FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Encounter, Patient } from '@medplum/fhirtypes';
import { EncounterModal } from '../encounters-tab/EncounterModal';
import { CompleteEncounterModal } from '../encounters-tab/CompleteEncounterModal';
import { CancelEncounterModal } from '../encounters-tab/CancelEncounterModal';
import {
  IconCircleFilled,
  IconPlus,
  IconX,
  IconCheck,
  IconClock,
  IconStethoscope,
  IconUser,
  IconUserCheck,
  IconUsers,
  IconNote,
  IconPencil,
} from '@tabler/icons-react';
import {
  calculateEncounterDuration,
  formatEncounterDuration,
  getConsultantPractitioners,
  getEncounterReason,
  getMainPractitioner,
  getReferringDoctor,
  getEncounterNotes,
} from '@utils/encounter.utils';

interface CurrentEncounterManagerProps {
  patient: Patient;
  currentEncounter: Encounter | undefined;
  setCurrentEncounter: (encounter: Encounter | undefined) => void;
}

export const CurrentEncounterManager: FC<CurrentEncounterManagerProps> = ({
  patient,
  currentEncounter,
  setCurrentEncounter,
}) => {
  const { t } = useTranslation(['encounters']);

  // Modal states
  const [modals, setModals] = useState({
    complete: false,
    cancel: false,
    newEncounter: false,
    edit: false,
  });

  // Modal handlers
  const openModal = (modalName: keyof typeof modals) => {
    setModals((prev) => ({ ...prev, [modalName]: true }));
  };

  const closeModal = (modalName: keyof typeof modals) => {
    setModals((prev) => ({ ...prev, [modalName]: false }));
  };

  // Encounter handlers
  const handleEncounterUpdated = useCallback(() => {
    setCurrentEncounter(undefined);
  }, [setCurrentEncounter]);

  const handleEncounterCreated = useCallback(
    (encounter: Encounter) => {
      setCurrentEncounter(encounter);
    },
    [setCurrentEncounter]
  );

  // Calculate encounter duration and extract additional information
  const encounterInfo = useMemo(() => {
    if (!currentEncounter) {
      return null;
    }

    const startTime = currentEncounter.period?.start
      ? new Date(currentEncounter.period.start)
      : new Date();

    const { hours, minutes } = calculateEncounterDuration(currentEncounter);

    return {
      startTime: startTime.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      }),
      duration: formatEncounterDuration(hours, minutes),
      type: currentEncounter.type?.[0]?.coding?.[0]?.display,
      reason: getEncounterReason(currentEncounter),
      referringDoctor: getReferringDoctor(currentEncounter),
      mainPractitioner: getMainPractitioner(currentEncounter),
      consultants: getConsultantPractitioners(currentEncounter),
      notes: getEncounterNotes(currentEncounter),
    };
  }, [currentEncounter]);

  // Render active encounter controls
  if (currentEncounter && encounterInfo) {
    return (
      <>
        <Paper p="md" bg="#f0fdf4" w="100%" withBorder radius="md">
          <Stack gap="sm">
            {/* Header with status indicator */}
            <Group justify="space-between" align="flex-start">
              <Group gap="xs">
                <IconCircleFilled size={12} color="#22c55e" />
                <Text size="sm" fw={500} c="green.7">
                  {t('inProgress', { ns: 'encounters' })}
                </Text>
              </Group>
              <ActionIcon variant="subtle" onClick={() => openModal('edit')}>
                <IconPencil size={18} />
              </ActionIcon>
            </Group>

            {/* Encounter type */}
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                <Text component="span" size="sm" fw={500}>
                  {t('detailsPage.encounterTypeLabel', { ns: 'encounters' })}:
                </Text>{' '}
                {encounterInfo.type}
              </Text>
            </Group>

            {/* Reason for visit */}
            {encounterInfo.reason && (
              <Group gap="xs">
                <IconStethoscope size={16} color="#6b7280" />
                <Text size="sm" c="dimmed">
                  <Text component="span" size="sm" fw={500}>
                    {t('reason')}:
                  </Text>{' '}
                  {encounterInfo.reason}
                </Text>
              </Group>
            )}

            {/* Main practitioner */}
            {encounterInfo.mainPractitioner && (
              <Group gap="xs">
                <IconUserCheck size={16} color="#6b7280" />
                <Text size="sm" c="dimmed">
                  <Text component="span" size="sm" fw={500}>
                    {t('practitioner', { ns: 'encounters' })}:
                  </Text>{' '}
                  {encounterInfo.mainPractitioner.display || t('unknownDoctor')}
                </Text>
              </Group>
            )}

            {/* Consultant practitioners */}
            {encounterInfo.consultants &&
              encounterInfo.consultants.length > 0 && (
                <Group gap="xs" align="flex-start" wrap="nowrap">
                  <Box mt={2} w={16}>
                    <IconUsers size={16} color="#6b7280" />
                  </Box>
                  <Text size="sm" c="dimmed">
                    <Text component="span" size="sm" fw={500}>
                      {t('additionalConsultants', { ns: 'encounters' })}:
                    </Text>{' '}
                    {encounterInfo.consultants
                      .map(
                        (consultant) => consultant.display || t('unknownDoctor')
                      )
                      .join(', ')}
                  </Text>
                </Group>
              )}

            {/* Referring doctor */}
            {encounterInfo.referringDoctor && (
              <Group gap="xs">
                <IconUser size={16} color="#6b7280" />
                <Text size="sm" c="dimmed">
                  <Text component="span" size="sm" fw={500}>
                    {t('referredBy')}:
                  </Text>{' '}
                  {encounterInfo.referringDoctor.display || t('unknownDoctor')}
                </Text>
              </Group>
            )}

            {/* Time information */}
            <Group gap="lg">
              <Group gap="xs">
                <IconClock size={16} color="#6b7280" />
                <Text size="sm" c="dimmed">
                  {t('startedAt', { ns: 'encounters' })}{' '}
                  {encounterInfo.startTime}
                </Text>
              </Group>
              <Text size="sm" fw={500}>
                {encounterInfo.duration}
              </Text>
            </Group>

            {encounterInfo.notes && (
              <Group gap="xs" align="flex-start" wrap="nowrap">
                <Box w={16} mt={3}>
                  <IconNote size={16} color="#6b7280" />
                </Box>
                <Text size="sm" c="dimmed">
                  <Text component="span" size="sm" fw={500}>
                    {t('detailsPage.notes', { ns: 'encounters' })}:
                  </Text>{' '}
                  {encounterInfo.notes}
                </Text>
              </Group>
            )}

            {/* Action buttons */}
            <Group gap="xs" justify="flex-end">
              <Button
                leftSection={<IconX size={16} />}
                variant="subtle"
                color="red"
                size="sm"
                onClick={() => openModal('cancel')}
              >
                {t('buttons.cancel', { ns: 'encounters' })}
              </Button>
              <Button
                leftSection={<IconCheck size={16} />}
                variant="filled"
                color="green"
                size="sm"
                onClick={() => openModal('complete')}
              >
                {t('complete.button', { ns: 'encounters' })}
              </Button>
            </Group>
          </Stack>
        </Paper>

        {/* Modals for active encounter */}
        <EncounterModal
          opened={modals.edit}
          onClose={() => closeModal('edit')}
          onEncounterCreated={handleEncounterCreated}
          patient={patient}
          encounter={currentEncounter}
        />
        <CompleteEncounterModal
          opened={modals.complete}
          onClose={() => closeModal('complete')}
          encounter={currentEncounter}
          onEncounterUpdated={handleEncounterUpdated}
        />
        <CancelEncounterModal
          opened={modals.cancel}
          onClose={() => closeModal('cancel')}
          encounter={currentEncounter}
          onEncounterUpdated={handleEncounterUpdated}
        />
      </>
    );
  }

  // Render new encounter button
  return (
    <>
      <Button
        leftSection={<IconPlus size={16} />}
        variant="light"
        size="sm"
        onClick={() => openModal('newEncounter')}
        fullWidth
      >
        {t('new', { ns: 'encounters' })}
      </Button>

      {/* Modal for new encounter */}
      <EncounterModal
        opened={modals.newEncounter}
        onClose={() => closeModal('newEncounter')}
        onEncounterCreated={handleEncounterCreated}
        patient={patient}
      />
    </>
  );
};
